import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";
import { flushSync } from "react-dom";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, Select, MenuItem, Tooltip } from "@mui/material";
import Barcode from "react-barcode";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import Print, { type PrintHandle} from "@components/print/print";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./print_barcode.scoped.scss";

interface State {
  selectedInfo: any,
}

const defState = {
  selectedInfo: {},
};

interface SearchParams {
  barcodeNameLike: string,
  page: number,
  pageSize: number
}

const defSearchParams = {
  barcodeNameLike: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

function PrintBarcode(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const printRef = useRef<PrintHandle>(null);
  const barcodeWrapRef = useRef<HTMLDivElement>(null);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  useLayoutEffect(() => {
    const svg = barcodeWrapRef.current?.querySelector("svg");
    if (!svg) return;
    const w = svg.getAttribute("width");
    const h = svg.getAttribute("height");
    if (w && h && !svg.getAttribute("viewBox")) {
      svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    }
    svg.removeAttribute("width");
    svg.removeAttribute("height");
  }, [state.selectedInfo.barcodeValue]);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "바코드명", headerClass: "hd-ta-left", field: "barcodeName", width: 200, cellStyle: { textAlign: "left"} },
    { headerName: "바코드값", headerClass: "hd-ta-left", field: "barcodeValue", width: 200, cellStyle: { textAlign: "left"} },
    { headerName: "설명", headerClass: "hd-ta-left", field: "barcodeDesc", width: 400, cellStyle: { textAlign: "left"} },
    { headerName: "출력", field: "", width: 100, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (!auth.download) return null;
        return (<button className="icon-button grid-print" onClick={() => print(p.data)}>출력</button>);
      }
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/master/print_barcode.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/master/print_barcode", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/master/print_barcode", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/master/print_barcode", {});
  }

  async function print(info: any) {
    flushSync(() => {
      setState(prev => ({ ...prev, selectedInfo: info }));
    });
    await printRef.current?.print();
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>바코드 출력</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>바코드명</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.barcodeNameLike}
              onChange={(e) => {updateSearchParams({ barcodeNameLike: e.target.value })}}
              onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
              placeholder="바코드명"
            />
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 그리드 */}
      </div>
      {/*// contents */}
      <Print ref={printRef}>
        <div className="print_barcode">
          <div ref={barcodeWrapRef} className="barcode-wrap">
            <Barcode value={state.selectedInfo.barcodeValue} format="CODE128" width={1} height={28} displayValue={false} background="transparent" margin={0} />
          </div>
          <p>{state.selectedInfo.barcodeName}</p>
          <span>({state.selectedInfo.barcodeValue})</span>
        </div>
      </Print>
    </>
  );
}

export default React.memo(PrintBarcode);
